/**
 * SelectEditController — the Select-sub-mode whole-object move drags
 * plus the marquee/lasso gesture, extracted from ConstructionTool
 * (#848, epic #828). A tool OWNS a controller instance and DRIVES it by
 * composition (it is NOT a base class), mirroring the
 * `VertexEditController` / `RotationController` / `DeleteSliceController`
 * pattern: the controller exclusively owns the four Select-mode
 * interaction STATE fields plus the drag/lasso-exclusive helpers, while
 * everything construction-specific it needs from the owning tool (scope
 * / scene / project / settings / commit / selection, the shared
 * live-snap machinery, the angle-lock translation, the drag-guide
 * rays, `translateDragClosure`, mirror routing, the lasso hit-test, the
 * drill-selection state, and the duplicate action) is injected through
 * the `SelectEditHost` interface.
 *
 * The controller owns four interaction-state fields exclusively:
 *   - `lineDrag`            — moving a single selected construction line;
 *   - `groupDrag`           — moving a multi-line / group / bbox
 *                             selection (frozen anchors, per-member
 *                             centres, bbox refs);
 *   - `altDuplicatePending` — deferred Alt-drag clone arming (#345);
 *   - `lasso`               — the marquee/lasso gesture + its overlay.
 *
 * ConstructionTool's `onDown` (the select sub-mode pick / drag / lasso
 * resolution) delegates to `maybeStartDrag`; its `onMove` / `onUp` /
 * `onCancel` delegate the four interactions, preserving the exact
 * original dispatch order (the onUp commit blocks are interleaved with
 * other controllers' releases, so they are dispatched via the three
 * distinct `handleAltDuplicateUp` / `handleLassoUp` / `handleUp` entry
 * points at their original positions).
 */
import paper from "paper";
import type { PointerSample } from "../canvas/PointerInput";
import type { Scene, SceneSelection } from "../canvas/Scene";
import {
  descendantConstructionImageIds,
  descendantConstructionLineIds,
  imageAncestorIds,
  lineAncestorIds,
  projectConstructionLines,
  projectTracingImages,
  type Project,
} from "../model/Project";
import { descendantLeafIds } from "../model/ConstructionObject";
import {
  BBOX_EDGES,
  getBboxCenter,
  getBboxMidpoint,
  hasCoincidentConstraint,
  propagateCoincidentAfterGroupMove,
  propagateCoincidentAfterLineMove,
  type BboxEdge,
  type SnapRefLite,
} from "../model/constraintEnforcement";
import { resolveOffsetRoot } from "../model/constructionOffset";
import {
  isMirrorDerived,
  type MirrorMatrix,
} from "../model/constructionMirror";
import { applyMirrorDelta } from "../model/connections";
import { showCanvasToast } from "../canvasToast";
import {
  isPinnableSnapRef,
  snapRefToConstraintRef,
  MIRROR_OFFSET_REFUSE_HINT,
} from "./constructionSnapRefs";
import { computeDragClosure } from "../model/dragClosure";
import { cssVar } from "../colors";
import {
  angleModifierHeld,
  isAngleSnapActive,
  isGridSnapActive,
  isPointConstraintActive,
  pickHandlePoint,
  snapPointToGrid,
} from "./dragSnap";
import {
  translateClosureInProject,
  translateConstructionLineInProject,
} from "./SelectTool";
import { newId } from "../model/ids";
import type { ToolSettings } from "./EditorContext";
import type { LiveSnapPair } from "./constructionSnapController";

const PREVIEW_STROKE = () => cssVar("--construction-preview");

/** Drill state for the canvas-click selection model (owned by the
 *  tool, shared with this controller through the host). */
export interface SelectDrillState {
  lineId: string;
  depth: number;
  timestamp: number;
  wasDrag: boolean;
}

/** Everything the SelectEditController needs from its owning tool. The
 *  four interaction-state fields + the drag/lasso-exclusive helpers
 *  live in the controller; the shared machinery (scene / project /
 *  settings / commit / selection, the live-snap tiers, the angle-lock
 *  translation + drag guide, mirror routing, the lasso hit-test, the
 *  drill-selection state, and the duplicate action) stays on
 *  ConstructionTool and is injected through this host. */
export interface SelectEditHost {
  // ---- core context -------------------------------------------------
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getProject(): Project;
  getSettings(): ToolSettings;
  commit(label: string, mutate: (proj: Project) => Project): void;
  setSelection(
    sel: SceneSelection,
    opts?: { autoPromoteGroups?: boolean },
  ): void;

  // ---- shared live-snap machinery -----------------------------------
  getLiveSnap(): LiveSnapPair | null;
  clearLiveSnap(): void;
  refreshLiveSnapOverlay(): void;
  updateLiveSnap(
    sources: Array<{ ref: SnapRefLite; pos: { x: number; y: number } }>,
    extraExcludedLineIds?: ReadonlySet<string>,
  ): { sourceRef: SnapRefLite; candidatePos: { x: number; y: number } } | null;

  // ---- shared helpers (stay on ConstructionTool) --------------------
  /** Rigidly translate every line in a pre-computed drag closure —
   *  SHARED with vertex-drag, so it stays on the tool. */
  translateDragClosure(
    closure: ReadonlySet<string>,
    originIds: ReadonlySet<string>,
    dx: number,
    dy: number,
  ): void;
  /** The object's TRANSLATION for an angle-locked drag (#725). */
  angleGridTranslation(
    start: { x: number; y: number },
    cursor: { x: number; y: number },
    ref: { x: number; y: number },
  ): { tx: number; ty: number };
  /** Show the angle-snap guide + HUD angle chip for the current drag
   *  frame (#725). */
  updateDragGuide(
    from: { x: number; y: number },
    to: { x: number; y: number },
    rawMoved: number,
    angleActive: boolean,
  ): void;
  /** Hide the angle-snap guide + HUD angle chip. */
  clearDragAngleGuide(): void;
  /** Route a drag that started on a mirror-derived line back to the
   *  source root (issue #180). Null when the chain hits an offset. */
  tryRouteMirrorLineDragToSource(
    hitLineId: string,
  ): { sourceRootId: string; matrices: MirrorMatrix[] } | null;
  /** Every visible construction object caught by the closed lasso
   *  polygon (lines AND tracing images), resolved in one pass. */
  objectsInsideLasso(points: ReadonlyArray<{ x: number; y: number }>): {
    lineIds: string[];
    imageIds: string[];
  };
  /** Duplicate the current selection (Alt-drag clone, #345). */
  duplicateConstructionSelection(opts: {
    offsetMm?: number;
  }): { lineIds: string[]; groupIds: string[]; imageIds: string[] } | null;
  /** Drill-selection state (owned by the tool). */
  getDrillState(): SelectDrillState | null;
  setDrillState(state: SelectDrillState | null): void;
}

export class SelectEditController {
  /** Window (ms) within which consecutive clicks on the same line
   *  count as drill continuation rather than fresh outermost-group
   *  picks. Roughly matches the OS-level double-click threshold. */
  private static readonly DRILL_WINDOW_MS = 500;

  /** Threshold (mm) for treating a click+release as a drag rather
   *  than a stationary click. A click that exceeded this distance
   *  before release does NOT contribute to drill continuation —
   *  dragging the outermost group should not be the "first click" of
   *  a drill sequence. */
  private static readonly DRILL_DRAG_THRESHOLD_MM = 1;

  // ---- exclusively-owned interaction state --------------------------

  /** Option/Alt-drag-to-duplicate (issue #345). Captured on `onDown`
   *  in the Select sub-mode when the user presses on a construction
   *  line / group with Alt held. The actual in-place duplicate + drag
   *  is deferred to the first `onMove` past the slop threshold so a
   *  plain Alt-click leaves no stray copy stacked on the original. */
  private altDuplicatePending: {
    hit: { constructionId?: string; constructionGroupId?: string };
    p: { x: number; y: number };
  } | null = null;

  // Move-line drag state — set in "select" sub-mode when the user
  // clicks somewhere on an existing line. Translates the rendered
  // item per-frame; commit on up via `translateConstructionLineInProject`.
  private lineDrag: {
    constructionId: string;
    start: { x: number; y: number };
    last: { x: number; y: number };
    item: paper.Item | null;
    // Frozen pre-drag anchor positions for every segment on the
    // moved line. Per-frame snap logic finds the closest pair
    // between (these + current translation) and anchors on other
    // lines, so it always works from the canonical pre-drag
    // geometry rather than the live-mutated rendered item.
    sourceAnchors: Array<{
      pathIndex: number;
      segmentIndex: number;
      pos: { x: number; y: number };
    }>;
    /** Pre-drag bbox-centre of the moved line if it carries a
     *  parametric primitive (rectangle / ellipse / pill). null for
     *  lines without a primitive. Used per-frame as an extra snap
     *  source so centre-to-centre snapping works during whole-line
     *  drags. */
    sourceCenter: { x: number; y: number } | null;
    /** Pre-drag bbox-edge midpoints of the moved line, keyed by
     *  edge. Empty when the line has no primitive — same lifecycle
     *  as `sourceCenter`. Used per-frame as extra snap sources so
     *  midpoint-to-anything snapping works during whole-line drags. */
    sourceMidpoints: Map<BboxEdge, { x: number; y: number }>;
    /** Pre-drag position of the "handle point" — the snap-able point
     *  on the line nearest to where the user touched at drag start.
     *  Picked once at onDown from anchors + centre + midpoints. Used
     *  by the Grid snap (issue #98 follow-up) to nudge that point to
     *  the nearest grid intersection as the cursor moves. */
    handleSource: { x: number; y: number } | null;
    /** Every line that translates rigidly with this drag — the
     *  dragged line plus every snap-chain partner and every offset
     *  descendant reachable through either edge type. Computed once
     *  at mouseDown via `computeDragClosure`; the per-frame loop
     *  just iterates and translates each non-origin member. Also
     *  doubles as the ghost-exclusion set for the snap-candidate
     *  search so moving lines don't surface as stale candidates
     *  (#102, extended for #29). */
    closure: Set<string>;
    /** Issue #180 bidirectional editing. When set, the user grabbed
     *  a mirror-derived line; the drag's `constructionId` was
     *  redirected to the mirror's source ROOT, and every per-frame
     *  cursor delta gets fed through this chain of axis reflection
     *  matrices (outermost-axis-first) to compute the SOURCE's
     *  delta. The mirror re-derives via
     *  `rederiveConnectionsAffectedBy` so it ends up tracking the
     *  cursor. Axis lines stay put because they're not in the
     *  closure (see `dragClosure.ts`). */
    bidirectionalMirrorMatrices?: MirrorMatrix[];
  } | null = null;

  /** Whole-group drag (issue #80 slice 2). Engaged when the user
   *  grabs a line whose owning group (or any ancestor group) is in
   *  `selection.constructionGroupIds`. Every member line listed in
   *  `lineIds` is rigidly translated together per frame; on commit
   *  every member's `pathData` (and primitive metadata, if any) is
   *  translated by the same delta and the cross-group constraint
   *  propagation handles external partners pinned to members.
   *
   *  Skipped for single-line selections — the existing `lineDrag`
   *  path covers that case unchanged. */
  private groupDrag: {
    /** The specific group whose members are being rigidly translated.
     *  Picked by `findGroupMembershipFor` as the deepest selected
     *  group containing the hit line, so a click on a leaf inside a
     *  nested subtree drags that subtree's overlay rather than the
     *  outer wrapper's. Used by the per-frame loop to translate the
     *  selection bbox + centre dot in lock-step with the lines.
     *
     *  `null` when the drag is an ad-hoc multi-line drag (issue
     *  #274) or a mixed selection drag (issue #295): the user has
     *  multiple things picked (lines + groups, or multiple groups,
     *  or an ad-hoc multi-line set) and grabbed any of them. The
     *  per-line translate logic is identical, but there's no single
     *  source-group overlay; selected-group overlays are tracked via
     *  `selectedGroupIds` below so each follows the drag in
     *  lock-step. */
    groupId: string | null;
    /** Every construction group that was in the selection when the
     *  drag started. The per-frame loop translates each group's
     *  bbox + centre-dot overlay in lock-step (issue #295). At
     *  commit, each group's stored rotation pivot is shifted only
     *  when its entire member set ended up in the drag — partial
     *  membership leaves the pivot alone since the group isn't
     *  moving as a unit. Empty for pure ad-hoc multi-line drags
     *  (#274) where no group is in the selection at all. */
    selectedGroupIds: string[];
    lineIds: string[];
    /** Tracing images dragged along with this group / selection
     *  (issue #509). Translated live via
     *  `Scene.translateTracingImageRender` and their `x`/`y` committed
     *  on release. Empty when no image is part of the drag. */
    imageIds: string[];
    /** Live Paper.js items keyed by line id so the per-frame loop
     *  can call `.translate(...)` without re-walking the rootLayer
     *  each frame. */
    items: Map<string, paper.Item>;
    /** Per-line frozen anchor positions, mirroring `lineDrag.sourceAnchors`
     *  but for every member. Used by the bbox-grab snap-source pass
     *  so per-member anchors stay snap targets while the group
     *  translates. */
    sourceAnchorsByLine: Map<
      string,
      Array<{
        pathIndex: number;
        segmentIndex: number;
        pos: { x: number; y: number };
      }>
    >;
    /** Frozen primitive bbox-centre of each member line that has a
     *  primitive (rectangle / ellipse / pill). Missing for hand-drawn
     *  member lines. Used as a snap source during bbox-grabbed
     *  drags so centre-to-centre snapping works across group
     *  members — mirrors `lineDrag.sourceCenter` but per-member. */
    sourceCentersByLine: Map<string, { x: number; y: number }>;
    /** Frozen primitive bbox-edge midpoints per member line. Same
     *  lifecycle as `sourceCentersByLine` — only primitive members
     *  contribute. Snap-source set for bbox-grabbed drags so the 4
     *  edge midpoints of each member primitive can pin onto external
     *  geometry the same way the centres do. */
    sourceMidpointsByLine: Map<string, Map<BboxEdge, { x: number; y: number }>>;
    /** True when this drag was initiated by grabbing the group's
     *  bbox frame (rather than a specific member line). Bbox-grabbed
     *  drags pick up snap-on-release: the frozen bbox corners +
     *  centre + every member anchor + every primitive bbox-centre
     *  are all snap sources, so the user can align the group as a
     *  unit against external geometry. Member-line drags keep the
     *  legacy "coarse move, no snap" behaviour. */
    bboxGrab: boolean;
    /** Frozen bbox corner + centre + edge-midpoint positions at
     *  drag-start, used as extra snap sources during a bbox-grabbed
     *  drag. Empty for non-bbox drags. Same project-mm units as
     *  `sourceAnchorsByLine`. */
    bboxRefs: Array<{
      kind:
        | "bbox-corner-tl"
        | "bbox-corner-tr"
        | "bbox-corner-bl"
        | "bbox-corner-br"
        | "bbox-center"
        | "bbox-mid-top"
        | "bbox-mid-right"
        | "bbox-mid-bottom"
        | "bbox-mid-left";
      pos: { x: number; y: number };
    }>;
    /** Pre-drag position of the snap-able point on the group nearest
     *  the user's initial touch. Same role as `lineDrag.handleSource`
     *  — drives Grid snap so the dragged group nudges the grabbed
     *  point onto the nearest grid intersection. */
    handleSource: { x: number; y: number } | null;
    start: { x: number; y: number };
    last: { x: number; y: number };
    /** Rigid-translation closure: every group member plus every
     *  snap-chain partner plus every offset descendant reachable
     *  via either edge type. Computed once at mouseDown so the
     *  per-frame loop is a fixed-cost translate pass. */
    closure: Set<string>;
  } | null = null;

  /** Lasso-select drag (issue #80 slice 4). Engaged in the "select"
   *  sub-mode when `onDown` lands on empty canvas. Captures
   *  pointer points as the user drags, renders a yellow dashed
   *  polyline on the construction overlay, and on release hit-tests
   *  every construction line against the closed polygon. The
   *  modifier active at down-time picks the combine mode so the
   *  semantics stay locked to the user's intent at gesture start
   *  even if they release a key mid-drag.
   *
   *  Click without drag (gesture stays inside the slop radius):
   *  "replace" → clear selection (the existing empty-tap behaviour);
   *  "add" / "toggle" → no-op so a stray Shift-click doesn't blow
   *  away the selection the user is building. */
  private lasso: {
    start: { x: number; y: number };
    points: Array<{ x: number; y: number }>;
    mode: "replace" | "add" | "toggle";
    startLineSel: string[];
    startGroupSel: string[];
    /** Tracing images selected when the lasso started (issue #522) —
     *  combined with the lasso's image hits per `mode`, same as lines. */
    startImageSel: string[];
    /** True once the gesture moved past the slop radius. Drives the
     *  click-vs-drag branch in `onUp`. */
    active: boolean;
    overlay: paper.Path | null;
  } | null = null;

  constructor(private host: SelectEditHost) {}

  // -------------------------------------------------------------------
  // Public accessors (thin tool delegators forward into these)
  // -------------------------------------------------------------------

  isLineDragActive(): boolean {
    return !!this.lineDrag;
  }
  isGroupDragActive(): boolean {
    return !!this.groupDrag;
  }
  isLassoActive(): boolean {
    return !!this.lasso;
  }

  /**
   * Keep the snap-candidate dots (#367) on a construction line while it is
   * being dragged, instead of parked where the drag started.
   *
   * Computes the in-flight project with the SAME translate fns and the same
   * total delta the commit uses below (`last - start`, mirror-adjusted), so the
   * dots sit exactly where release will leave them. Costs nothing when the dots
   * are switched off, which is the default.
   */
  private refreshLiveSnapCandidates(): void {
    const drag = this.lineDrag;
    if (!drag) return;
    const scene = this.host.getScene();
    if (!scene.isSnapCandidatesVisible()) return;
    let dx = drag.last.x - drag.start.x;
    let dy = drag.last.y - drag.start.y;
    if (drag.bidirectionalMirrorMatrices) {
      let d = { x: dx, y: dy };
      for (const m of drag.bidirectionalMirrorMatrices)
        d = applyMirrorDelta(m, d);
      dx = d.x;
      dy = d.y;
    }
    const scope = this.host.getScope();
    const moved = translateConstructionLineInProject(
      this.host.getProject(),
      drag.constructionId,
      dx,
      dy,
      scope,
    );
    scene.setSnapCandidateLiveProject(
      translateClosureInProject(
        moved,
        drag.closure,
        new Set([drag.constructionId]),
        dx,
        dy,
        scope,
      ),
    );
  }

  /** Drop every in-flight Select-mode interaction. Called from the
   *  tool's sub-mode / picker switches, onDeactivate and onCancel so a
   *  half-finished move / lasso doesn't bleed across (clears the lasso
   *  overlay too). */
  reset(): void {
    this.lineDrag = null;
    this.groupDrag = null;
    this.altDuplicatePending = null;
    // Hand the dots back to the committed model (see refreshLiveSnapCandidates).
    this.host.getScene().setSnapCandidateLiveProject(null);
    if (this.lasso) {
      this.clearLassoOverlay(this.lasso);
      this.lasso = null;
    }
  }

  // -------------------------------------------------------------------
  // Pointer-flow delegation (called by ConstructionTool)
  // -------------------------------------------------------------------

  /** onDown delegation for the select sub-mode pick / drag / lasso
   *  resolution. Runs AFTER the tool's rotation / resize / pivot
   *  guards (which stay on the tool). Resolves the hit into a
   *  selection change and starts the appropriate move drag / alt-
   *  duplicate arming / lasso. Always consumes the press (the original
   *  select block returned unconditionally). */
  maybeStartDrag(
    s: PointerSample,
    p: { x: number; y: number },
    pt: paper.Point,
  ): void {
    // Select sub-tool: clicking a line picks it (and starts a move
    // drag); clicking empty space starts a lasso. Modifier-click
    // on a line toggles / adds to the multi-selection without
    // starting a drag. If the line belongs to a currently-
    // selected group (issue #80 slice 2 — group as rigid object)
    // we start a `groupDrag` that translates every member of
    // that group together instead of the single line. Falls back
    // to `lineDrag` otherwise so unselected lines and ungrouped
    // lines behave as before.
    const hit = this.host.getScene().hitTest(pt);
    const sel = this.host.getScene().getSelection();
    const isAdd = s.shiftKey;
    const isToggle = s.metaKey || s.ctrlKey;
    const isModifier = isAdd || isToggle;
    // Option/Alt-drag-to-duplicate (issue #345): a body grab with Alt
    // held (and no select-modifier) arms a duplicate. The clone +
    // drag is deferred to the first `onMove` past the slop so a plain
    // Alt-click doesn't strand a copy on top of the original. Lands
    // only on a line / group hit — rotation handles already claimed
    // the press above, and empty space falls through to the lasso.
    if (
      s.altKey &&
      !isModifier &&
      (hit.constructionId || hit.constructionGroupId)
    ) {
      this.altDuplicatePending = {
        hit: {
          constructionId: hit.constructionId,
          constructionGroupId: hit.constructionGroupId,
        },
        p: { x: p.x, y: p.y },
      };
      return;
    }
    // Bbox grab: clicking on the selected-group frame starts a
    // whole-group drag without needing to land on a specific
    // member line. The bbox is only rendered for selected groups,
    // so this branch fires before the per-line hit handler — a
    // line happens to overlap with the frame stroke would still
    // route through the bbox path (the frame sits on top in
    // z-order). Bbox-grabbed drags also pick up snap-on-release
    // against external geometry; see `startGroupDrag`.
    //
    // Issue #295: when other items are in the selection too (other
    // groups, or bare lines), the drag set expands to the full
    // selection union. The hit group's specific `groupId` is kept
    // only when the selection is exactly that group with no
    // additions — otherwise the drag is treated as ad-hoc multi
    // (groupId=null) and snap labels / bbox-frame snap sources fall
    // back to the multi-line behaviour.
    if (hit.constructionGroupId) {
      const { lineIds: unionLineIds, imageIds: unionImageIds } =
        this.expandSelectionToObjectDragSet(sel);
      if (unionLineIds.length > 0 || unionImageIds.length > 0) {
        const hitMembers = descendantLeafIds(
          this.host.getProject(),
          hit.constructionGroupId,
        );
        const hitGroupMembers = new Set(hitMembers.lineIds);
        const hitGroupImages = new Set(hitMembers.imageIds);
        const selectedGroupIds = sel.constructionGroupIds ?? [];
        const isPureSingleGroup =
          selectedGroupIds.length === 1 &&
          selectedGroupIds[0] === hit.constructionGroupId &&
          unionLineIds.every((id) => hitGroupMembers.has(id)) &&
          unionImageIds.every((id) => hitGroupImages.has(id));
        this.startGroupDrag(
          isPureSingleGroup ? hit.constructionGroupId : null,
          unionLineIds,
          p,
          { bboxGrab: true, selectedGroupIds, imageIds: unionImageIds },
        );
        return;
      }
    }
    if (hit.constructionId) {
      const selectedLineIds = sel.constructionIds ?? [];
      const selectedGroupIds = sel.constructionGroupIds ?? [];
      const isInSel = selectedLineIds.includes(hit.constructionId);
      // Shift-dragging a line that's ALREADY in the selection moves it
      // (with angle-lock, #725) instead of re-selecting it — fall through
      // to the drag path below. Cmd/Ctrl-toggle, and Shift on a
      // not-yet-selected line, still just extend / toggle the multi-
      // selection without starting a drag.
      if (isModifier && !(isAdd && isInSel)) {
        // Modifier-click on a line: extend / toggle the multi-
        // selection without starting a drag. Selected groups are
        // preserved so the user can layer line picks on top of a
        // group-level selection.
        let nextLines: string[];
        if (isAdd) {
          nextLines = isInSel
            ? selectedLineIds
            : [...selectedLineIds, hit.constructionId];
        } else {
          // Cmd/Ctrl-click: toggle in/out.
          nextLines = isInSel
            ? selectedLineIds.filter((id) => id !== hit.constructionId)
            : [...selectedLineIds, hit.constructionId];
        }
        this.host.setSelection({
          pieceId: null,
          decorationId: null,
          constructionIds: nextLines,
          constructionGroupIds: selectedGroupIds,
        });
        return;
      }
      // Multi-line / mixed-selection drag-together (#274 + #295):
      // when the user grabs the body of a line that's already in
      // the selection — directly, or as a descendant of a selected
      // group — translate the WHOLE selection union together.
      // Modifier-click already returned above, so this branch
      // never fights with extend / toggle gestures. The
      // single-line single-selection case stays on `lineDrag`
      // below so per-anchor / per-midpoint / per-bbox-centre snap
      // sources keep working as before.
      //
      // Pure single-group case (one group selected, no extras) is
      // delegated to the drill path below so the existing
      // group-bbox snap labels + rotation pivot tracking apply —
      // identical to today's behaviour. Any other shape (multi-
      // line, multi-group, or group + bare line) takes the union
      // path with `groupId=null`.
      const { lineIds: unionLineIds, imageIds: unionImageIds } =
        this.expandSelectionToObjectDragSet(sel);
      const unionContainsHit = unionLineIds.includes(hit.constructionId);
      const isPureSingleGroup =
        selectedGroupIds.length === 1 &&
        selectedLineIds.every((id) => unionLineIds.includes(id)) &&
        unionLineIds.every((id) =>
          descendantConstructionLineIds(
            this.host.getProject(),
            selectedGroupIds[0]!,
          ).includes(id),
        );
      if (unionContainsHit && unionLineIds.length > 1 && !isPureSingleGroup) {
        this.startGroupDrag(null, unionLineIds, p, {
          selectedGroupIds,
          imageIds: unionImageIds,
        });
        // Ad-hoc / mixed multi-drag is not a drill — clear any
        // pending drill state so a subsequent plain-click on a
        // member doesn't get treated as the 2nd of a drill
        // sequence.
        this.host.setDrillState(null);
        return;
      }
      // Drill-aware single-click. Walks the line's ancestor group
      // chain (outermost → … → direct parent → line) and resolves
      // the click to chain[drillDepth]: depth 0 = outermost group,
      // each samesite continuation bumps depth one deeper, up to
      // the leaf line itself. When depth lands on a group, the
      // selection is the GROUP entity (with all descendant lines
      // highlighted) and the drag becomes a group drag; landing on
      // the leaf line gives a single-line selection + lineDrag so
      // a drilled-in user can manipulate one member at a time.
      const ancestors = lineAncestorIds(
        this.host.getProject(),
        hit.constructionId,
      );
      const chain: string[] = [...ancestors, hit.constructionId];
      const now = performance.now();
      const drillState = this.host.getDrillState();
      const samesite =
        drillState !== null &&
        drillState.lineId === hit.constructionId &&
        !drillState.wasDrag &&
        now - drillState.timestamp < SelectEditController.DRILL_WINDOW_MS;
      // Initial depth resolution:
      //   - samesite continuation → bump prior depth one level
      //     deeper (the classic double-click drill).
      //   - otherwise → derive from the CURRENT selection so a
      //     pick made elsewhere (layers panel, programmatic setter,
      //     properties panel) survives a canvas click on the same
      //     item. Without this step, a leaf selected via the layers
      //     panel would silently re-promote to the outermost group
      //     the moment the user grabs it on canvas to drag it.
      const depth = Math.min(
        samesite
          ? drillState!.depth + 1
          : this.deriveDrillDepthFromSelection(chain, sel),
        chain.length - 1,
      );
      const targetId = chain[depth]!;
      const targetIsGroup = depth < chain.length - 1;
      this.host.setDrillState({
        lineId: hit.constructionId,
        depth,
        timestamp: now,
        wasDrag: false,
      });
      if (targetIsGroup) {
        const memberLineIds = descendantConstructionLineIds(
          this.host.getProject(),
          targetId,
        );
        const memberImageIds = descendantConstructionImageIds(
          this.host.getProject(),
          targetId,
        );
        // Per-item selection model (#875): selecting the GROUP makes
        // it the active transform unit but does NOT blanket-select its
        // member lines — `constructionIds` stays empty so the members
        // aren't individually highlighted and each remains
        // independently clickable (drill to a leaf, or a layers child
        // row) to edit its own properties. Transforms still act on the
        // whole group because every transform path fans the group out
        // to its members via `constructionGroupIds` (see
        // `expandSelectionToObjectDragSet`, `descendantLeafIds`).
        //
        // Drill chose a specific level intentionally — disable
        // auto-promote so the selection stays at the chosen group.
        this.host.setSelection(
          {
            pieceId: null,
            decorationId: null,
            constructionIds: [],
            constructionGroupIds: [targetId],
            tracingImageIds: memberImageIds,
          },
          { autoPromoteGroups: false },
        );
        this.startGroupDrag(targetId, memberLineIds, p, {
          imageIds: memberImageIds,
        });
        return;
      }
      // Leaf line target: single-line selection + lineDrag. The
      // drilled-in state lets the user drag one member at a time
      // without the whole group following along. Auto-promote is
      // explicitly disabled here — the user's intent in drilling
      // to a leaf is to act on just that line, so promoting an
      // enclosing group whose only member is this leaf would
      // defeat the drill.
      this.host.setSelection(
        {
          pieceId: null,
          decorationId: null,
          constructionId: hit.constructionId,
          constructionGroupIds: [],
        },
        { autoPromoteGroups: false },
      );
      // Suppress the unused-variable check for the existing
      // selectedGroupIds reference (kept around so the modifier
      // branch above still compiles unchanged).
      void selectedGroupIds;
      // Dragging an offset line drags its entire cluster — the
      // root source it follows, every snap-chain partner of that
      // source, and every transitive offset all translate as one
      // rigid block. Redirect the lineDrag to the root and let
      // the pre-computed drag closure carry the cluster.
      //
      // Dragging a MIRROR-DERIVED line (issue #180 bidirectional)
      // also redirects to the source root, but per-frame deltas
      // must be reflected through each axis in the mirror chain.
      // `tryRouteMirrorLineDragToSource` walks the chain and
      // collects the matrices; null result means the chain hits
      // an offset (V1 refuses bidirectional through offset math)
      // — early-return so the geometry stays put.
      const hitLine = projectConstructionLines(this.host.getProject()).find(
        (l) => l.id === hit.constructionId,
      );
      let bidirectionalMirrorMatrices: MirrorMatrix[] | undefined;
      let dragLineId: string;
      if (hitLine && isMirrorDerived(hitLine)) {
        const route = this.host.tryRouteMirrorLineDragToSource(
          hit.constructionId,
        );
        if (!route) {
          // Mirror-of-offset (or chain broken) — refuse the drag
          // with a brief hint so the user knows the no-op was
          // intentional.
          showCanvasToast(MIRROR_OFFSET_REFUSE_HINT);
          return;
        }
        dragLineId = route.sourceRootId;
        bidirectionalMirrorMatrices = route.matrices;
      } else {
        dragLineId = resolveOffsetRoot(
          this.host.getProject(),
          hit.constructionId,
        );
      }
      const item = this.host.getScene().getConstructionLineItem(dragLineId);
      // Capture every anchor's pre-drag project position from
      // the live rendered item so per-frame snap-candidate
      // search can compute "where each anchor would be after the
      // pending translation" without re-parsing pathData each
      // frame.
      const sourceAnchors: Array<{
        pathIndex: number;
        segmentIndex: number;
        pos: { x: number; y: number };
      }> = [];
      if (item) {
        const cp = item as paper.CompoundPath;
        const subs: paper.Path[] =
          cp.children && cp.children.length > 0
            ? (cp.children as paper.Path[])
            : [item as unknown as paper.Path];
        subs.forEach((sub, pathIndex) => {
          sub.segments.forEach((seg, segmentIndex) => {
            sourceAnchors.push({
              pathIndex,
              segmentIndex,
              pos: { x: seg.point.x, y: seg.point.y },
            });
          });
        });
      }
      const lineForCenter = projectConstructionLines(
        this.host.getProject(),
      ).find((l) => l.id === dragLineId);
      const sourceCenter = lineForCenter
        ? getBboxCenter(lineForCenter, this.host.getScope())
        : null;
      const sourceMidpoints = new Map<BboxEdge, { x: number; y: number }>();
      if (lineForCenter) {
        for (const edge of BBOX_EDGES) {
          const m = getBboxMidpoint(lineForCenter, edge, this.host.getScope());
          if (m) sourceMidpoints.set(edge, m);
        }
      }
      // Handle point = the snap-able point on the line nearest the
      // grab. Drives the Grid snap (issue #98 follow-up) so the
      // dragged shape feels like it "carries" that specific point
      // onto the grid rather than nudging an arbitrary anchor.
      const handleSource = pickHandlePoint(p, {
        anchors: sourceAnchors.map((sa) => sa.pos),
        center: sourceCenter,
        midpoints: [...sourceMidpoints.values()],
      });
      // Pre-compute the rigid-translation closure once: the
      // dragged line plus every snap-chain partner (transitively)
      // plus every offset descendant of any line in the cluster.
      // The set doesn't change mid-drag (constraints are mutated
      // only inside `ctx.commit`), so the per-frame loop just
      // iterates this set.
      const closure = computeDragClosure(this.host.getProject(), [dragLineId]);
      this.lineDrag = {
        constructionId: dragLineId,
        start: { x: p.x, y: p.y },
        last: { x: p.x, y: p.y },
        item,
        sourceAnchors,
        sourceCenter,
        sourceMidpoints,
        handleSource,
        ...(bidirectionalMirrorMatrices ? { bidirectionalMirrorMatrices } : {}),
        closure,
      };
    } else if (this.host.getScene().hitTestTracingImage(pt)) {
      // Tracing image grab (issue #509). The locked raster is
      // invisible to Paper's hit-test (so drawing tools draw through
      // it), so we resolve the hit geometrically. Construction lines
      // take priority — this branch only runs when no line was hit.
      const imgHit = this.host.getScene().hitTestTracingImage(pt)!;
      const state = this.host.getProject();
      const selectedImageIds = sel.tracingImageIds ?? [];
      if (isModifier) {
        // Modifier-click: toggle / add the image into the multi-
        // select set without starting a drag. Lines + groups in the
        // selection are preserved so images and lines can be picked
        // together.
        const isIn = selectedImageIds.includes(imgHit);
        let nextImages: string[];
        if (isAdd) {
          nextImages = isIn ? selectedImageIds : [...selectedImageIds, imgHit];
        } else {
          nextImages = isIn
            ? selectedImageIds.filter((id) => id !== imgHit)
            : [...selectedImageIds, imgHit];
        }
        this.host.setSelection({
          pieceId: null,
          decorationId: null,
          constructionIds: sel.constructionIds ?? [],
          constructionGroupIds: sel.constructionGroupIds ?? [],
          tracingImageIds: nextImages,
        });
        return;
      }
      // Drag-together when the image is already part of a multi-
      // member selection (with other images / lines / groups).
      const { lineIds: unionLineIds, imageIds: unionImageIds } =
        this.expandSelectionToObjectDragSet(sel);
      if (
        unionImageIds.includes(imgHit) &&
        unionLineIds.length + unionImageIds.length > 1
      ) {
        this.startGroupDrag(null, unionLineIds, p, {
          selectedGroupIds: sel.constructionGroupIds ?? [],
          imageIds: unionImageIds,
        });
        this.host.setDrillState(null);
        return;
      }
      // Grouped image → select + drag the outermost enclosing group
      // as a rigid unit (the layers panel / ungroup is the path to
      // move a single grouped image on its own).
      const ancestors = imageAncestorIds(state, imgHit);
      if (ancestors.length > 0) {
        const groupId = ancestors[0]!;
        const { lineIds: memberLineIds, imageIds: memberImageIds } =
          descendantLeafIds(state, groupId);
        // Per-item selection model (#875): the group is the transform
        // unit, but its member lines are NOT individually selected —
        // `constructionIds` stays empty so members aren't blanket-
        // highlighted (transforms still fan the group out to members).
        this.host.setSelection(
          {
            pieceId: null,
            decorationId: null,
            constructionIds: [],
            constructionGroupIds: [groupId],
            tracingImageIds: memberImageIds,
          },
          { autoPromoteGroups: false },
        );
        this.startGroupDrag(groupId, memberLineIds, p, {
          imageIds: memberImageIds,
        });
        this.host.setDrillState(null);
        return;
      }
      // Ungrouped single image → select it alone and start its move.
      this.host.setSelection({
        pieceId: null,
        decorationId: null,
        tracingImageId: imgHit,
      });
      this.startGroupDrag(null, [], p, { imageIds: [imgHit] });
      this.host.setDrillState(null);
      return;
    } else {
      // Empty-space tap → start a lasso. The selection mode
      // (replace / add / toggle) is captured from the modifier
      // keys held at down-time so subsequent key changes during
      // the drag don't shift the semantics mid-stroke. A
      // gesture that never moves past the slop is treated as a
      // plain click and collapses to "clear selection" when in
      // replace mode (no-op for add / toggle).
      const mode: "replace" | "add" | "toggle" = isAdd
        ? "add"
        : isToggle
          ? "toggle"
          : "replace";
      this.lasso = {
        start: { x: p.x, y: p.y },
        points: [{ x: p.x, y: p.y }],
        mode,
        startLineSel: sel.constructionIds ?? [],
        startGroupSel: sel.constructionGroupIds ?? [],
        startImageSel: sel.tracingImageIds ?? [],
        active: false,
        overlay: null,
      };
      // Empty-space click breaks any drill sequence — the user is
      // either clearing the selection or starting a lasso. Either
      // way, the next per-line click should resolve fresh from
      // depth 0.
      this.host.setDrillState(null);
    }
  }

  /** onMove delegation: advance whichever Select-mode interaction is
   *  live. Returns true when one consumed the move. Mirrors the
   *  original in-order dispatch (lasso → alt-duplicate-pending →
   *  group → line); the alt-duplicate-pending branch arms a group
   *  drag then falls through to the group block. */
  handleMove(p: { x: number; y: number }, s: PointerSample): boolean {
    if (this.lasso) {
      const lasso = this.lasso;
      const dx = p.x - lasso.start.x;
      const dy = p.y - lasso.start.y;
      // 3 mm slop — same threshold the other Select / Edit gestures
      // use to distinguish click from drag. Below the threshold no
      // overlay renders so a quick tap doesn't flash a tiny loop.
      const SLOP = 3;
      if (!lasso.active && Math.hypot(dx, dy) < SLOP) return true;
      lasso.active = true;
      lasso.points.push({ x: p.x, y: p.y });
      this.refreshLassoOverlay();
      return true;
    }
    if (this.altDuplicatePending) {
      // Option/Alt-drag (#345): wait for real movement, then clone the
      // selection in place and hand the copies to the regular group
      // drag below — which advances with this same frame's cursor so
      // the copy tracks the pointer from the start.
      const dx = p.x - this.altDuplicatePending.p.x;
      const dy = p.y - this.altDuplicatePending.p.y;
      if (Math.hypot(dx, dy) < 3) return true;
      const pending = this.altDuplicatePending;
      this.altDuplicatePending = null;
      if (!this.beginAltDuplicateDrag(pending.hit, pending.p)) return true;
      // Falls through to the `if (this.groupDrag)` block below.
    }
    if (this.groupDrag) {
      // Whole-group drag (#80). Both bbox-grabbed and member-line
      // grabbed drags live-snap (#861): every member anchor, every
      // member primitive centre and every per-member bbox-edge
      // midpoint are snap sources, so the user can align the group as
      // a unit against external geometry — just like a single-line
      // drag. Bbox-grabbed drags additionally offer the 4 bbox corners
      // and the bbox centre as snap sources. Snapping honours the
      // snap-master / point / line / grid toggles (via updateLiveSnap
      // + the grid fallback), so it stays off when the user turns it
      // off.
      const drag = this.groupDrag;
      let targetX = p.x;
      let targetY = p.y;
      {
        // Live point-snap for EVERY group drag — bbox-grabbed AND
        // member-line-grabbed (#861). Every member anchor, every
        // member primitive centre and every per-member bbox-edge
        // midpoint is a snap source so the group can align as a unit
        // against external geometry, exactly like a single-line drag
        // (`lineDrag` below). `updateLiveSnap` already honours the
        // snap-master / point / line toggles, so snapping stays off
        // when the user has it off — no forced snap. Bbox-grabbed
        // drags ADDITIONALLY offer the 4 bbox corners + bbox centre
        // as snap sources (see below); member-line grabs don't, since
        // there's no visible bbox handle being dragged.
        const proposedDx = p.x - drag.start.x;
        const proposedDy = p.y - drag.start.y;
        const sources: Array<{
          ref: SnapRefLite;
          pos: { x: number; y: number };
        }> = [];
        for (const [lid, anchors] of drag.sourceAnchorsByLine) {
          for (const a of anchors) {
            sources.push({
              ref: {
                kind: "anchor",
                constructionId: lid,
                pathIndex: a.pathIndex,
                segmentIndex: a.segmentIndex,
              },
              pos: { x: a.pos.x + proposedDx, y: a.pos.y + proposedDy },
            });
          }
        }
        for (const [lid, c] of drag.sourceCentersByLine) {
          sources.push({
            ref: { kind: "center", constructionId: lid },
            pos: { x: c.x + proposedDx, y: c.y + proposedDy },
          });
        }
        // Per-member bbox-edge midpoints — same snap-source story
        // as the centres above. Positions come from the frozen
        // `sourceMidpointsByLine` snapshot taken at drag-start so
        // the per-frame loop avoids reparsing primitive metadata.
        for (const [lid, mids] of drag.sourceMidpointsByLine) {
          for (const [edge, m] of mids) {
            sources.push({
              ref: { kind: "bbox-midpoint", constructionId: lid, edge },
              pos: { x: m.x + proposedDx, y: m.y + proposedDy },
            });
          }
        }
        if (drag.bboxGrab) {
          // The 4 bbox corners + bbox centre as synthetic anchor refs.
          // We pin them under one made-up constructionId so
          // `findNearestSnapCandidate`'s same-line exclusion doesn't
          // accidentally drop real-line candidates; `pathIndex` 0–4
          // encodes which bbox ref (tl/tr/bl/br/center) — see
          // `BBOX_REF_KIND_BY_PATH_INDEX` below.
          const bboxConstructionId = `__bbox:${drag.groupId}`;
          drag.bboxRefs.forEach((br, i) => {
            sources.push({
              ref: {
                kind: "anchor",
                constructionId: bboxConstructionId,
                pathIndex: i,
                segmentIndex: 0,
              },
              pos: { x: br.pos.x + proposedDx, y: br.pos.y + proposedDy },
            });
          });
        }
        // Every line in the pre-computed drag closure is moving with
        // this group drag — exclude all of them from snap-candidate
        // search so stale pre-drag positions don't surface as ghosts
        // (#102, extended for #29 offsets).
        const snap = this.host.updateLiveSnap(sources, drag.closure);
        if (snap) {
          const sourceOrig = this.resolveGroupDragSourcePos(
            drag,
            snap.sourceRef,
          );
          if (sourceOrig) {
            const adjustedDx = snap.candidatePos.x - sourceOrig.x;
            const adjustedDy = snap.candidatePos.y - sourceOrig.y;
            targetX = drag.start.x + adjustedDx;
            targetY = drag.start.y + adjustedDy;
          }
        } else if (
          isGridSnapActive(this.host.getSettings()) &&
          drag.handleSource
        ) {
          // Grid snap fallback — same shape as the lineDrag branch.
          const grid = this.host.getScene().getMatGridInfo();
          if (grid) {
            const handleNow = {
              x: drag.handleSource.x + proposedDx,
              y: drag.handleSource.y + proposedDy,
            };
            const gridPt = snapPointToGrid(handleNow, grid);
            const adjustedDx = gridPt.x - drag.handleSource.x;
            const adjustedDy = gridPt.y - drag.handleSource.y;
            targetX = drag.start.x + adjustedDx;
            targetY = drag.start.y + adjustedDy;
          }
        }
      }
      // Angle-lock (#725): with Shift held OR the angle-snap toggle on,
      // constrain the whole-group move to the nearest common drafting angle
      // (0/30/45/60/90/…). If grid-snap is also on, slide the endpoint along
      // that ray onto the nearest grid-line crossing. Applied after point/grid
      // snap so the angle constraint always wins.
      const angleActive = isAngleSnapActive(
        this.host.getSettings(),
        angleModifierHeld(s),
      );
      if (angleActive) {
        const ref = drag.handleSource ?? drag.start;
        const t = this.host.angleGridTranslation(drag.start, p, ref);
        targetX = drag.start.x + t.tx;
        targetY = drag.start.y + t.ty;
      }
      // Always show the move vector + distance from the grabbed reference.
      const gref = drag.handleSource ?? drag.start;
      this.host.updateDragGuide(
        gref,
        {
          x: gref.x + (targetX - drag.start.x),
          y: gref.y + (targetY - drag.start.y),
        },
        Math.hypot(p.x - drag.start.x, p.y - drag.start.y),
        angleActive,
      );
      const dx = targetX - drag.last.x;
      const dy = targetY - drag.last.y;
      if (dx !== 0 || dy !== 0) {
        // Translate each member's yellow stroke AND its halo in
        // lock-step (#85). The cached `items` map is still useful
        // as a "did this member render?" check (members rendered
        // before the group selection landed have entries; lazy
        // renders won't).
        for (const lineId of drag.items.keys()) {
          this.host.getScene().translateConstructionLineRender(lineId, dx, dy);
          // Live MIRROR update only — NOT a full connection-rederive.
          // Group drag translates every member rigidly, including any
          // offset descendant that happens to be a group member; a
          // subsequent `rederiveOffsetsOf(source)` would then RE-COMPUTE
          // the offset's pathData from the source's already-translated
          // position, double-translating it by `(dx, dy)` (regression
          // covered by `offset.spec.ts:1098`). Mirrors don't double-up
          // because they're not included in group selection (mirror-
          // derived lines can't be group members today).
          this.host.getScene().rederiveMirrorsAffectedBy(lineId);
        }
        // Group selection bbox(es) follow the rigid translation in
        // lock-step — same lag class as #85's halo: without this
        // the overlay only catches up on commit and "pops" to the
        // new union bounds on release. Iterates every group that
        // was in the selection at drag-start so mixed drags
        // (#295 — multiple groups, or group + bare line) keep
        // every visible bbox attached. Ad-hoc multi-line drags
        // (#274) have an empty `selectedGroupIds` and the loop
        // no-ops.
        for (const gid of drag.selectedGroupIds) {
          this.host.getScene().translateConstructionGroupOverlay(gid, dx, dy);
        }
        // Member tracing images (issue #509) ride along with the move,
        // their raster + selection frame translating in lock-step.
        for (const imageId of drag.imageIds) {
          this.host.getScene().translateTracingImageRender(imageId, dx, dy);
        }
        // Pivot crosshair (issue #420) rides along with the group move.
        this.host.getScene().translatePivotMarkers(dx, dy);
        drag.last = { x: targetX, y: targetY };
        // Translate every closure line except the members already
        // translated above. The closure was computed once at
        // mouseDown — no per-frame graph re-walk. Includes every
        // member's snap-chain partner, every offset descendant of
        // any moving line, transitively.
        this.host.translateDragClosure(
          drag.closure,
          new Set(drag.lineIds),
          dx,
          dy,
        );
        // Snap-candidate rings render from live segment positions —
        // refresh after the closure pass for every group drag (#861),
        // matching the single-line drag below so member-grabbed drags
        // show the same snap feedback.
        this.host.refreshLiveSnapOverlay();
      }
      return true;
    }
    if (this.lineDrag && this.lineDrag.item) {
      // Cursor-implied translation from the drag start. We compute
      // the snap candidate against this proposed translation,
      // then adjust the *actual* applied delta so the snapping
      // ref lands exactly on its candidate (instead of just
      // following the cursor and visibly jumping at release).
      const proposedDx = p.x - this.lineDrag.start.x;
      const proposedDy = p.y - this.lineDrag.start.y;
      const sources: Array<{
        ref: SnapRefLite;
        pos: { x: number; y: number };
      }> = this.lineDrag.sourceAnchors.map((sa) => ({
        ref: {
          kind: "anchor" as const,
          constructionId: this.lineDrag!.constructionId,
          pathIndex: sa.pathIndex,
          segmentIndex: sa.segmentIndex,
        },
        pos: { x: sa.pos.x + proposedDx, y: sa.pos.y + proposedDy },
      }));
      // Whole-line drag of a primitive also offers the line's
      // centre as a snap source so centre-to-centre snapping works.
      if (this.lineDrag.sourceCenter) {
        sources.push({
          ref: {
            kind: "center" as const,
            constructionId: this.lineDrag.constructionId,
          },
          pos: {
            x: this.lineDrag.sourceCenter.x + proposedDx,
            y: this.lineDrag.sourceCenter.y + proposedDy,
          },
        });
      }
      // The 4 bbox-edge midpoints of a primitive are also snap
      // sources during a whole-line drag — same lifecycle as the
      // centre source above, so any of them can snap onto another
      // line's anchor / centre / midpoint.
      for (const [edge, mid] of this.lineDrag.sourceMidpoints) {
        sources.push({
          ref: {
            kind: "bbox-midpoint" as const,
            constructionId: this.lineDrag.constructionId,
            edge,
          },
          pos: { x: mid.x + proposedDx, y: mid.y + proposedDy },
        });
      }
      // Every line in the pre-computed drag closure is moving with
      // this drag — exclude all of them from snap-candidate search
      // so their stale pre-drag positions don't surface as ghosts
      // (#102, extended for #29 offsets).
      const snap = this.host.updateLiveSnap(sources, this.lineDrag.closure);
      let targetX = p.x;
      let targetY = p.y;
      if (snap) {
        // Resolve the snap source's pre-drag position — anchor,
        // centre, or one of the 4 bbox-edge midpoints — so we can
        // solve `candidatePos = source.original + adjustedDelta`.
        let sourceOrigPos: { x: number; y: number } | null = null;
        const sourceRef = snap.sourceRef;
        if (sourceRef.kind === "anchor") {
          const sourceOrig = this.lineDrag.sourceAnchors.find(
            (sa) =>
              sa.pathIndex === sourceRef.pathIndex &&
              sa.segmentIndex === sourceRef.segmentIndex,
          );
          if (sourceOrig) sourceOrigPos = sourceOrig.pos;
        } else if (sourceRef.kind === "center") {
          if (this.lineDrag.sourceCenter) {
            sourceOrigPos = this.lineDrag.sourceCenter;
          }
        } else if (sourceRef.kind === "bbox-midpoint") {
          sourceOrigPos =
            this.lineDrag.sourceMidpoints.get(sourceRef.edge) ?? null;
        }
        // A segment-midpoint never appears as a drag SOURCE (the moving
        // line only contributes anchors / centre / bbox midpoints), so
        // there's no branch for it — `sourceOrigPos` stays null.
        if (sourceOrigPos) {
          const adjustedDx = snap.candidatePos.x - sourceOrigPos.x;
          const adjustedDy = snap.candidatePos.y - sourceOrigPos.y;
          targetX = this.lineDrag.start.x + adjustedDx;
          targetY = this.lineDrag.start.y + adjustedDy;
        }
      } else if (
        isGridSnapActive(this.host.getSettings()) &&
        this.lineDrag.handleSource
      ) {
        // No point-snap fired this frame, but Grid snap is on: nudge
        // the handle point (the snap-able point nearest the user's
        // initial touch) onto the closest grid intersection. Point
        // snap wins when present because landing on another shape's
        // anchor is a more specific intent than grid alignment.
        const grid = this.host.getScene().getMatGridInfo();
        if (grid) {
          const handleNow = {
            x: this.lineDrag.handleSource.x + proposedDx,
            y: this.lineDrag.handleSource.y + proposedDy,
          };
          const gridPt = snapPointToGrid(handleNow, grid);
          const adjustedDx = gridPt.x - this.lineDrag.handleSource.x;
          const adjustedDy = gridPt.y - this.lineDrag.handleSource.y;
          targetX = this.lineDrag.start.x + adjustedDx;
          targetY = this.lineDrag.start.y + adjustedDy;
        }
      }
      // Angle-lock (#725): with Shift held OR the angle-snap toggle on,
      // constrain the whole-line (and its closure) move to the nearest common
      // drafting angle (0/30/45/60/90/…); if grid-snap is also on, slide the
      // endpoint along that ray onto the nearest grid-line crossing. Applied
      // after point/grid snap so it always wins, and in cursor space before
      // the mirror reflection below.
      const lineAngleActive = isAngleSnapActive(
        this.host.getSettings(),
        angleModifierHeld(s),
      );
      if (lineAngleActive) {
        const ref = this.lineDrag.handleSource ?? this.lineDrag.start;
        const t = this.host.angleGridTranslation(this.lineDrag.start, p, ref);
        targetX = this.lineDrag.start.x + t.tx;
        targetY = this.lineDrag.start.y + t.ty;
      }
      // Always show the move vector + distance from the grabbed reference —
      // except on a bidirectional-mirror drag, where target is in mirror space
      // and the reference is the source's, so a guide would mislead.
      if (this.lineDrag.bidirectionalMirrorMatrices) {
        this.host.clearDragAngleGuide();
      } else {
        const gref = this.lineDrag.handleSource ?? this.lineDrag.start;
        this.host.updateDragGuide(
          gref,
          {
            x: gref.x + (targetX - this.lineDrag.start.x),
            y: gref.y + (targetY - this.lineDrag.start.y),
          },
          Math.hypot(p.x - this.lineDrag.start.x, p.y - this.lineDrag.start.y),
          lineAngleActive,
        );
      }
      let dx = targetX - this.lineDrag.last.x;
      let dy = targetY - this.lineDrag.last.y;
      // Issue #180 bidirectional editing: the user grabbed a
      // mirror-derived line; the drag's `constructionId` is the
      // source ROOT (set in onDown via `tryRouteMirrorLineDragToSource`).
      // The user sees the cursor on the MIRROR's location; the SOURCE
      // moves by the reflected delta. Each axis matrix in the chain
      // is applied to the cursor delta (outermost-first) to get the
      // source-space delta. Snap candidates are NOT computed for
      // bidirectional drags in V1 — they'd reference the source's
      // anchors, not the mirror's, which would confuse the user.
      if (this.lineDrag.bidirectionalMirrorMatrices) {
        let d = { x: dx, y: dy };
        for (const m of this.lineDrag.bidirectionalMirrorMatrices) {
          d = applyMirrorDelta(m, d);
        }
        dx = d.x;
        dy = d.y;
      }
      if (dx !== 0 || dy !== 0) {
        // Translate the dragged line itself (yellow stroke + halo
        // in lock-step, #85). Every other moving line — snap-chain
        // partners, offset descendants, transitive — rides along
        // via the closure translate below.
        const movedRoot = this.host
          .getScene()
          .translateConstructionLineRender(
            this.lineDrag.constructionId,
            dx,
            dy,
          );
        if (!movedRoot && this.lineDrag.item) {
          const scope = this.host.getScope();
          this.lineDrag.item.translate(new scope.Point(dx, dy));
        }
        this.lineDrag.last = { x: targetX, y: targetY };
        // Rigidly translate every closure line except the origin
        // (already translated above). The closure was computed once
        // at mouseDown — no per-frame graph re-walk.
        this.host.translateDragClosure(
          this.lineDrag.closure,
          new Set([this.lineDrag.constructionId]),
          dx,
          dy,
        );
        // Pivot crosshair tracks the move live (issue #420). Skipped
        // for bidirectional-mirror drags, where `dx,dy` is the
        // reflected SOURCE delta and the selected (mirror) line moves
        // by the cursor delta instead — the marker re-renders
        // correctly on commit.
        if (!this.lineDrag.bidirectionalMirrorMatrices) {
          this.host.getScene().translatePivotMarkers(dx, dy);
        }
        // Same idea for the snap-candidate dots (#367): they mark this line's
        // own anchors, segment midpoints and bbox centre, so leaving them at
        // the pre-drag position while the line slides away makes them read as
        // marks on empty canvas.
        this.refreshLiveSnapCandidates();
        // Live connection update (issue #180 unification): a whole-
        // line drag moves the source rigidly; the Connections
        // registry refreshes every dependent (mirror uses this line
        // as source OR axis, offset descendants, …) from the live
        // pathData so they all track the cursor.
        this.host
          .getScene()
          .rederiveConnectionsAffectedBy(this.lineDrag.constructionId);
        // Snap-candidate rings render from live segment positions —
        // refresh after the closure pass so they land on the post-
        // chain anchor positions instead of last frame's spots.
        this.host.refreshLiveSnapOverlay();
      }
      return true;
    }
    return false;
  }

  /** onUp delegation for the deferred Alt-duplicate no-op clear
   *  (#345). Dispatched EARLY (before the other release handlers), so
   *  a plain Alt-press that never moved past the slop drops its
   *  pending state and is treated as a no-op click rather than
   *  stranding a copy on the original. Returns true when consumed. */
  handleAltDuplicateUp(): boolean {
    if (this.altDuplicatePending) {
      this.altDuplicatePending = null;
      return true;
    }
    return false;
  }

  /** onUp delegation for the lasso commit. Returns true when a lasso
   *  gesture was live and got committed. */
  handleLassoUp(): boolean {
    if (this.lasso) {
      const lasso = this.lasso;
      this.lasso = null;
      this.clearLassoOverlay(lasso);
      this.commitLassoSelection(lasso);
      return true;
    }
    return false;
  }

  /** onUp delegation: commit whichever move drag is live and clear
   *  its state. Returns true when one consumed the release. Mirrors
   *  the original in-order dispatch (group → line); the two states
   *  are mutually exclusive (a single onDown seeds exactly one). */
  handleUp(p: { x: number; y: number }, s: PointerSample): boolean {
    void p;
    void s;
    if (this.groupDrag) {
      const drag = this.groupDrag;
      this.groupDrag = null;
      this.host.clearLiveSnap();
      this.host.clearDragAngleGuide();
      // Mark drill state as "this click went into a drag" so the
      // very next click on the same line doesn't get treated as the
      // 2nd of a double-click — dragging the outermost group is not
      // a drill-in gesture.
      const drillState = this.host.getDrillState();
      if (drillState) {
        const moved = Math.hypot(
          drag.last.x - drag.start.x,
          drag.last.y - drag.start.y,
        );
        if (moved > SelectEditController.DRILL_DRAG_THRESHOLD_MM) {
          drillState.wasDrag = true;
        }
      }
      const dx = drag.last.x - drag.start.x;
      const dy = drag.last.y - drag.start.y;
      if (Math.abs(dx) >= 0.001 || Math.abs(dy) >= 0.001) {
        const scope = this.host.getScope();
        const memberIds = new Set(drag.lineIds);
        const imageIdSet = new Set(drag.imageIds);
        const commitLabel =
          drag.groupId !== null
            ? "Move construction group"
            : drag.lineIds.length === 0 && drag.imageIds.length > 0
              ? "Move tracing image"
              : "Move construction lines";
        this.host.commit(commitLabel, (proj) => {
          // Translate every member line first.
          let moved = proj;
          for (const lid of drag.lineIds) {
            moved = translateConstructionLineInProject(
              moved,
              lid,
              dx,
              dy,
              scope,
            );
          }
          // Translate every closure line (snap-chain partners +
          // offset descendants reachable from any member). The
          // closure was computed once at mouseDown so live and
          // commit translate exactly the same set.
          const movedWithClosure = translateClosureInProject(
            moved,
            drag.closure,
            memberIds,
            dx,
            dy,
            scope,
          );
          // Final alignment pass — idempotent for already-satisfied
          // constraints; snaps partners onto members for any
          // unsatisfied constraint that touches a moved member.
          const enforced = propagateCoincidentAfterGroupMove(
            movedWithClosure,
            movedWithClosure.constructionLines ?? [],
            [...memberIds],
            scope,
          );
          // Shift the stored rotation pivot of every group whose
          // entire member set translated, so each tilted bbox stays
          // attached to its moved cluster (issue #67 follow-up,
          // generalised by issue #295). Groups whose members were
          // only partially dragged are left untouched — the group
          // wasn't moved as a unit, so the pivot would no longer
          // sit relative to a meaningful frame.
          const groups = (movedWithClosure.constructionGroups ?? []).map(
            (g) => {
              // The custom pivot (issue #420) rides along even on a
              // never-rotated group, so don't early-out on a missing
              // rotationCenter.
              if (!g.rotationCenter && !g.pivotPoint) return g;
              const groupMembers = descendantConstructionLineIds(
                movedWithClosure,
                g.id,
              );
              if (groupMembers.length === 0) return g;
              const allInDrag = groupMembers.every((id) => memberIds.has(id));
              if (!allInDrag) return g;
              return {
                ...g,
                rotationCenter: g.rotationCenter
                  ? {
                      x: g.rotationCenter.x + dx,
                      y: g.rotationCenter.y + dy,
                    }
                  : undefined,
                pivotPoint: g.pivotPoint
                  ? {
                      x: g.pivotPoint.x + dx,
                      y: g.pivotPoint.y + dy,
                    }
                  : undefined,
              };
            },
          );
          // Translate every dragged tracing image (issue #509) by the
          // same delta, committing its new placement.
          const tracingImages =
            imageIdSet.size > 0
              ? projectTracingImages(movedWithClosure).map((img) =>
                  imageIdSet.has(img.id)
                    ? {
                        ...img,
                        x: img.x + dx,
                        y: img.y + dy,
                        // The custom rotation pivot (#420) rides along on a
                        // move so it stays attached to the image.
                        ...(img.pivotPoint
                          ? {
                              pivotPoint: {
                                x: img.pivotPoint.x + dx,
                                y: img.pivotPoint.y + dy,
                              },
                            }
                          : {}),
                      }
                    : img,
                )
              : movedWithClosure.tracingImages;
          return {
            ...movedWithClosure,
            constructionGroups: groups,
            constructionLines: enforced,
            tracingImages,
          };
        });
      }
      return true;
    }
    if (this.lineDrag) {
      const drag = this.lineDrag;
      this.lineDrag = null;
      // The commit below writes the same translation the dots have been
      // previewing, so drop the live override and let them read the model.
      this.host.getScene().setSnapCandidateLiveProject(null);
      const snapAtRelease = this.host.getLiveSnap();
      this.host.clearLiveSnap();
      this.host.clearDragAngleGuide();
      // `last` is updated to the snapped target during the drag
      // (when within snap range) or the raw cursor position
      // otherwise — using it here means the committed translation
      // matches what's visible on screen.
      let dx = drag.last.x - drag.start.x;
      let dy = drag.last.y - drag.start.y;
      // Issue #180 bidirectional editing: the cursor delta is in
      // MIRROR space; the source moves by the reflected delta. Same
      // matrix chain the per-frame onMove applied — must run here
      // too so the commit translates the source by the SAME amount
      // the live drag did, otherwise the commit reverts the per-
      // frame work and the source slides back.
      if (drag.bidirectionalMirrorMatrices) {
        let d = { x: dx, y: dy };
        for (const m of drag.bidirectionalMirrorMatrices) {
          d = applyMirrorDelta(m, d);
        }
        dx = d.x;
        dy = d.y;
      }
      const drillState = this.host.getDrillState();
      if (drillState) {
        const moved = Math.hypot(dx, dy);
        if (moved > SelectEditController.DRILL_DRAG_THRESHOLD_MM) {
          drillState.wasDrag = true;
        }
      }
      if (Math.abs(dx) >= 0.001 || Math.abs(dy) >= 0.001) {
        const scope = this.host.getScope();
        this.host.commit("Move construction line", (proj) => {
          // Translate the dragged line first, then every other
          // closure member (snap-chain partners and offset
          // descendants reachable via either edge type, all
          // transitively). The closure was computed once at
          // mouseDown and matches the set the live-drag pass moved
          // — so live and commit stay in lock-step. Derived
          // offsets get re-derived from their (now-translated)
          // sources by `deriveConstructionOffsets` in the commit
          // wrapper.
          const moved = translateConstructionLineInProject(
            proj,
            drag.constructionId,
            dx,
            dy,
            scope,
          );
          const movedWithClosure = translateClosureInProject(
            moved,
            drag.closure,
            new Set([drag.constructionId]),
            dx,
            dy,
            scope,
          );
          // Append a new coincident constraint if the release-time
          // snap had a candidate. Dedupes against existing pairs.
          // Live drag already adjusted the delta so the source-ref
          // lands exactly on the candidate, so no further model
          // translation is needed for the newly-pinned partner.
          const projWithSnap =
            snapAtRelease &&
            isPointConstraintActive(this.host.getSettings()) &&
            isPinnableSnapRef(snapAtRelease.source) &&
            isPinnableSnapRef(snapAtRelease.candidate) &&
            !hasCoincidentConstraint(
              movedWithClosure,
              snapAtRelease.source,
              snapAtRelease.candidate,
            )
              ? {
                  ...movedWithClosure,
                  constraints: [
                    ...(movedWithClosure.constraints ?? []),
                    {
                      id: newId(),
                      kind: "coincident" as const,
                      refs: [
                        snapRefToConstraintRef(snapAtRelease.source),
                        snapRefToConstraintRef(snapAtRelease.candidate),
                      ],
                    },
                  ],
                }
              : movedWithClosure;
          // Run the snap-chain propagation as a final alignment
          // pass — for constraints that were satisfied pre-drag
          // every per-edge delta is zero (the closure already
          // moved both endpoints by the same drag delta) and this
          // is an idempotent no-op. For constraints that were NOT
          // satisfied pre-drag (e.g. a centre-coincident seeded
          // between lines at different positions), propagation
          // snaps the partner onto the moved source — preserving
          // the historical "commit enforces constraints" contract.
          const enforced = propagateCoincidentAfterLineMove(
            projWithSnap,
            projWithSnap.constructionLines ?? [],
            drag.constructionId,
            scope,
          );
          return { ...projWithSnap, constructionLines: enforced };
        });
      }
      return true;
    }
    return false;
  }

  // -------------------------------------------------------------------
  // Drag/lasso-exclusive helpers (moved with the concern)
  // -------------------------------------------------------------------

  /** The full set of construction objects that should travel with a
   *  selection drag (issues #295 / #509): every directly-selected line
   *  and image, plus every line and image inside any selected group.
   *  One walk over the selected groups via `descendantLeafIds`, returning
   *  the two id lists together — the line and image drag sets are always
   *  needed as a pair, so resolving them once keeps callers in sync. */
  private expandSelectionToObjectDragSet(sel: {
    constructionIds?: string[];
    tracingImageIds?: string[];
    constructionGroupIds?: string[];
  }): { lineIds: string[]; imageIds: string[] } {
    const lineSet = new Set<string>(sel.constructionIds ?? []);
    const imageSet = new Set<string>(sel.tracingImageIds ?? []);
    const proj = this.host.getProject();
    for (const gid of sel.constructionGroupIds ?? []) {
      const members = descendantLeafIds(proj, gid);
      for (const lid of members.lineIds) lineSet.add(lid);
      for (const iid of members.imageIds) imageSet.add(iid);
    }
    return { lineIds: [...lineSet], imageIds: [...imageSet] };
  }

  /** Resolve the initial drill depth from the **current selection**
   *  for a fresh click (i.e. when no in-flight `drillState`
   *  continuation applies). Lets a selection made via the layers
   *  panel, properties panel, or programmatic setter survive a
   *  canvas click on the same target — without this, the click
   *  would silently snap selection back to the outermost group
   *  (depth 0) every time, breaking the user's subselected state.
   *
   *  Resolution order, deepest-wins:
   *  - If any selected construction group is in the click's chain,
   *    use the **deepest** such group's index — preserves "user
   *    drilled into G" while still expanding to G's contents when
   *    they grab a member.
   *  - Else if the selected line is exactly the click target (and
   *    no group entity is selected), treat the click as a leaf
   *    pick — depth = chain.length - 1.
   *  - Else default to 0 (fresh outermost-group pick). */
  private deriveDrillDepthFromSelection(
    chain: readonly string[],
    sel: {
      constructionId?: string | null;
      constructionIds?: string[];
      constructionGroupIds?: string[];
    },
  ): number {
    const selectedGroupSet = new Set(sel.constructionGroupIds ?? []);
    let deepestGroupIdx = -1;
    for (let i = 0; i < chain.length - 1; i++) {
      if (selectedGroupSet.has(chain[i]!)) {
        deepestGroupIdx = i;
      }
    }
    if (deepestGroupIdx >= 0) return deepestGroupIdx;
    const leafId = chain[chain.length - 1]!;
    if (
      sel.constructionId === leafId &&
      (sel.constructionGroupIds ?? []).length === 0
    ) {
      return chain.length - 1;
    }
    return 0;
  }

  /** Map a snap-source ref back to its pre-drag project-mm position
   *  for a bbox-grabbed group drag. Three families of source ref
   *  exist:
   *  - Real anchor (`kind: "anchor"`, real constructionId, pathIndex,
   *    segmentIndex) → look up in `sourceAnchorsByLine`.
   *  - Real primitive centre (`kind: "center"`, real constructionId)
   *    → look up in `sourceCentersByLine`.
   *  - Synthetic bbox ref (`kind: "anchor"`, constructionId
   *    `__bbox:${groupId}`, pathIndex 0–4) → index into `bboxRefs`.
   *
   *  Returns null when the ref doesn't resolve — defensive for
   *  ephemeral state. */
  private resolveGroupDragSourcePos(
    drag: NonNullable<typeof this.groupDrag>,
    ref: SnapRefLite,
  ): { x: number; y: number } | null {
    if (ref.kind === "center") {
      return drag.sourceCentersByLine.get(ref.constructionId) ?? null;
    }
    if (ref.kind === "bbox-midpoint") {
      return (
        drag.sourceMidpointsByLine.get(ref.constructionId)?.get(ref.edge) ??
        null
      );
    }
    if (ref.constructionId === `__bbox:${drag.groupId}`) {
      return drag.bboxRefs[ref.pathIndex]?.pos ?? null;
    }
    const anchors = drag.sourceAnchorsByLine.get(ref.constructionId);
    if (!anchors) return null;
    const match = anchors.find(
      (a) =>
        a.pathIndex === ref.pathIndex && a.segmentIndex === ref.segmentIndex,
    );
    return match?.pos ?? null;
  }

  /** Option/Alt-drag-to-duplicate (issue #345). Clones the current
   *  selection exactly in place (zero offset), then starts a rigid
   *  group drag of the fresh copies so they track the cursor while the
   *  originals stay put — a pointer alternative to the Duplicate
   *  button / Cmd+D. When the pressed object isn't already in the
   *  selection, it's selected first (its outermost group if it has
   *  one, so the whole group duplicates) — mirroring how a plain grab
   *  resolves what to move. Returns true once the copies' drag is
   *  armed. */
  private beginAltDuplicateDrag(
    hit: { constructionId?: string; constructionGroupId?: string },
    p: { x: number; y: number },
  ): boolean {
    const proj = this.host.getProject();
    const sel = this.host.getScene().getSelection();
    const dragSet = new Set(this.expandSelectionToObjectDragSet(sel).lineIds);
    const selectedGroupIds = sel.constructionGroupIds ?? [];
    const hitInSelection = hit.constructionGroupId
      ? selectedGroupIds.includes(hit.constructionGroupId)
      : hit.constructionId
        ? dragSet.has(hit.constructionId)
        : false;
    if (!hitInSelection) {
      if (hit.constructionGroupId) {
        // Group is the transform unit; members not individually
        // selected (#875). Duplicate fans the group out via
        // `constructionGroupIds`, so an empty `constructionIds` still
        // clones the whole group.
        this.host.setSelection(
          {
            pieceId: null,
            decorationId: null,
            constructionIds: [],
            constructionGroupIds: [hit.constructionGroupId],
          },
          { autoPromoteGroups: false },
        );
      } else if (hit.constructionId) {
        const outermost = lineAncestorIds(proj, hit.constructionId)[0];
        if (outermost) {
          this.host.setSelection(
            {
              pieceId: null,
              decorationId: null,
              constructionIds: [],
              constructionGroupIds: [outermost],
            },
            { autoPromoteGroups: false },
          );
        } else {
          this.host.setSelection(
            {
              pieceId: null,
              decorationId: null,
              constructionId: hit.constructionId,
              constructionGroupIds: [],
            },
            { autoPromoteGroups: false },
          );
        }
      }
    }
    const result = this.host.duplicateConstructionSelection({ offsetMm: 0 });
    if (!result) return false;
    const cloneSel = this.host.getScene().getSelection();
    const { lineIds: cloneLineIds, imageIds: cloneImageIds } =
      this.expandSelectionToObjectDragSet(cloneSel);
    if (cloneLineIds.length === 0 && cloneImageIds.length === 0) return false;
    // A duplicate-drag is never a drill continuation.
    this.host.setDrillState(null);
    this.startGroupDrag(null, cloneLineIds, p, {
      selectedGroupIds: cloneSel.constructionGroupIds ?? [],
      imageIds: cloneImageIds,
    });
    return true;
  }

  /** Initialise a `groupDrag` with frozen rendered items + per-line
   *  anchor snapshots for the given member set. Called from the
   *  select-sub-mode hit handler when the click landed on a member
   *  of a selected group, OR on a member of an ad-hoc multi-line
   *  selection (issue #274 — `groupId` is `null` in that case). */
  private startGroupDrag(
    groupId: string | null,
    memberLineIds: readonly string[],
    p: { x: number; y: number },
    opts: {
      bboxGrab?: boolean;
      selectedGroupIds?: readonly string[];
      imageIds?: readonly string[];
    } = {},
  ): void {
    const items = new Map<string, paper.Item>();
    const sourceAnchorsByLine = new Map<
      string,
      Array<{
        pathIndex: number;
        segmentIndex: number;
        pos: { x: number; y: number };
      }>
    >();
    const sourceCentersByLine = new Map<string, { x: number; y: number }>();
    const sourceMidpointsByLine = new Map<
      string,
      Map<BboxEdge, { x: number; y: number }>
    >();
    const proj = this.host.getProject();
    const linesIndex = new Map(
      projectConstructionLines(proj).map((l) => [l.id, l] as const),
    );
    for (const lid of memberLineIds) {
      const item = this.host.getScene().getConstructionLineItem(lid);
      if (!item) continue;
      items.set(lid, item);
      const cp = item as paper.CompoundPath;
      const subs: paper.Path[] =
        cp.children && cp.children.length > 0
          ? (cp.children as paper.Path[])
          : [item as unknown as paper.Path];
      const anchors: Array<{
        pathIndex: number;
        segmentIndex: number;
        pos: { x: number; y: number };
      }> = [];
      subs.forEach((sub, pathIndex) => {
        sub.segments.forEach((seg, segmentIndex) => {
          anchors.push({
            pathIndex,
            segmentIndex,
            pos: { x: seg.point.x, y: seg.point.y },
          });
        });
      });
      sourceAnchorsByLine.set(lid, anchors);
      const lineRecord = linesIndex.get(lid);
      if (lineRecord) {
        const c = getBboxCenter(lineRecord, this.host.getScope());
        if (c) sourceCentersByLine.set(lid, c);
        const mids = new Map<BboxEdge, { x: number; y: number }>();
        for (const edge of BBOX_EDGES) {
          const m = getBboxMidpoint(lineRecord, edge, this.host.getScope());
          if (m) mids.set(edge, m);
        }
        if (mids.size > 0) sourceMidpointsByLine.set(lid, mids);
      }
    }
    // Bbox refs (corners + centre) are captured at drag-start so the
    // snap pipeline can use them as sources even after the per-frame
    // translate moves the overlay. The 4 corners + centre come from
    // the live overlay's union bounds — already computed by Scene
    // for the visible frame, so we just read it back here.
    const bboxRefs: NonNullable<typeof this.groupDrag>["bboxRefs"] = [];
    if (opts.bboxGrab) {
      // Snap sources for bbox corners + centre + 4 edge midpoints
      // derive from the GEOMETRIC union of member line bounds —
      // not from `overlay.bounds`, which includes the halo's stroke
      // overhang (≈ 1.6 mm beyond the actual union) and would land
      // snaps a half-stroke-width away from the intended point.
      let union: paper.Rectangle | null = null;
      for (const lid of memberLineIds) {
        const item = this.host.getScene().getConstructionLineItem(lid);
        if (!item) continue;
        union = union ? union.unite(item.bounds) : item.bounds;
      }
      if (union) {
        const cx = union.center.x;
        const cy = union.center.y;
        bboxRefs.push(
          { kind: "bbox-corner-tl", pos: { x: union.x, y: union.y } },
          {
            kind: "bbox-corner-tr",
            pos: { x: union.x + union.width, y: union.y },
          },
          {
            kind: "bbox-corner-bl",
            pos: { x: union.x, y: union.y + union.height },
          },
          {
            kind: "bbox-corner-br",
            pos: { x: union.x + union.width, y: union.y + union.height },
          },
          { kind: "bbox-center", pos: { x: cx, y: cy } },
          { kind: "bbox-mid-top", pos: { x: cx, y: union.y } },
          {
            kind: "bbox-mid-right",
            pos: { x: union.x + union.width, y: cy },
          },
          {
            kind: "bbox-mid-bottom",
            pos: { x: cx, y: union.y + union.height },
          },
          { kind: "bbox-mid-left", pos: { x: union.x, y: cy } },
        );
      }
    }
    // Handle point for Grid snap (#98 follow-up). Considers every
    // anchor / centre / midpoint across every member, plus the
    // bbox refs for bbox-grabbed drags. Falls back to the raw
    // touch position when the group somehow has no candidates.
    const handleCandidates: Array<{ x: number; y: number }> = [];
    for (const anchors of sourceAnchorsByLine.values()) {
      for (const a of anchors) handleCandidates.push(a.pos);
    }
    for (const c of sourceCentersByLine.values()) handleCandidates.push(c);
    for (const mids of sourceMidpointsByLine.values()) {
      for (const m of mids.values()) handleCandidates.push(m);
    }
    for (const br of bboxRefs) handleCandidates.push(br.pos);
    const handleSource = pickHandlePoint(p, { anchors: handleCandidates }) ?? {
      x: p.x,
      y: p.y,
    };
    // Closure of every member's snap-chain partners + offset
    // descendants. Computed once at mouseDown; the per-frame loop
    // just translates each closure line by the frame delta.
    const closure = computeDragClosure(this.host.getProject(), [
      ...memberLineIds,
    ]);
    // Selected groups whose overlays should follow the drag. Default
    // fallback for single-group drags (no explicit opt passed) is
    // `[groupId]` so the historical bbox/overlay tracking keeps
    // working unchanged.
    const selectedGroupIds =
      opts.selectedGroupIds !== undefined
        ? [...opts.selectedGroupIds]
        : groupId !== null
          ? [groupId]
          : [];
    this.groupDrag = {
      groupId,
      selectedGroupIds,
      lineIds: [...memberLineIds],
      imageIds: opts.imageIds ? [...opts.imageIds] : [],
      items,
      sourceAnchorsByLine,
      sourceCentersByLine,
      sourceMidpointsByLine,
      bboxGrab: opts.bboxGrab === true,
      bboxRefs,
      handleSource,
      start: { x: p.x, y: p.y },
      last: { x: p.x, y: p.y },
      closure,
    };
  }

  /** Re-paints the lasso polyline overlay from `this.lasso.points`.
   *  Called on every `onMove` past the slop threshold so the overlay
   *  tracks the cursor live. The polyline is ephemeral
   *  (`data.ephemeral = true`) so the next scene render leaves it
   *  alone, and ignored by hit-tests for the same reason. */
  private refreshLassoOverlay(): void {
    if (!this.lasso) return;
    const scope = this.host.getScope();
    scope.activate();
    if (this.lasso.overlay) {
      this.lasso.overlay.remove();
      this.lasso.overlay = null;
    }
    if (this.lasso.points.length < 2) return;
    const segs = this.lasso.points.map((pt) => new scope.Point(pt.x, pt.y));
    const path = new scope.Path({ segments: segs });
    path.strokeColor = new scope.Color(PREVIEW_STROKE());
    path.strokeWidth = 1.4;
    path.strokeScaling = false;
    path.dashArray = [3, 2];
    path.fillColor = null;
    path.closed = false;
    path.data = { ephemeral: true, kind: "construction-lasso" };
    this.lasso.overlay = path;
  }

  private clearLassoOverlay(lasso: NonNullable<typeof this.lasso>): void {
    if (lasso.overlay) {
      lasso.overlay.remove();
      lasso.overlay = null;
    }
  }

  /** Convert the captured lasso into a selection. Click without drag
   *  (`!active`) clears the selection in "replace" mode and is a
   *  no-op otherwise. An active drag closes the polygon, intersects
   *  every construction line against it, and combines the hits with
   *  the start selection per mode. */
  private commitLassoSelection(lasso: NonNullable<typeof this.lasso>): void {
    if (!lasso.active) {
      if (lasso.mode === "replace") {
        this.host.setSelection({
          pieceId: null,
          decorationId: null,
          constructionIds: [],
          constructionGroupIds: [],
          tracingImageIds: [],
        });
      }
      return;
    }
    const { lineIds: hits, imageIds: imageHits } = this.host.objectsInsideLasso(
      lasso.points,
    );
    // Combine each axis with the start selection per mode. Lines and
    // images (issue #522) follow identical replace / add / toggle
    // semantics so a lasso grabs both at once.
    const combine = (start: string[], found: string[]): string[] => {
      if (lasso.mode === "replace") return found;
      const set = new Set(start);
      if (lasso.mode === "add") {
        for (const id of found) set.add(id);
      } else {
        for (const id of found) {
          if (set.has(id)) set.delete(id);
          else set.add(id);
        }
      }
      return Array.from(set);
    };
    const nextLines = combine(lasso.startLineSel, hits);
    const nextImages = combine(lasso.startImageSel, imageHits);
    const nextGroups = lasso.mode === "replace" ? [] : [...lasso.startGroupSel];
    this.host.setSelection({
      pieceId: null,
      decorationId: null,
      constructionIds: nextLines,
      constructionGroupIds: nextGroups,
      tracingImageIds: nextImages,
    });
  }
}
